import { Link, useLocation, useNavigate } from 'react-router-dom'
import { isOnlineOrRecent } from '@utils/isOnlineOrRecent'
import { colors, fontSize, size } from '@styles/theme'
import useDrawerContext from '@contexts/drawerContext'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useYesOrNo from '@hooks/useYesOrNo'
import styled from '@emotion/styled'

import MessagesBottomDrawerContents from '@components/layout/bottom_drawers/MessagesBottomDrawerContents'
import { TbCarambola, TbCarambolaFilled, TbDotsVertical } from 'react-icons/tb'
import AtAGlanceDetails from '@components/nearby/AtAGlanceDetails'
import { ButtonBase, IconButton, } from '@mui/material'
import { PiCaretLeftBold } from 'react-icons/pi'
import YesOrNo from '@elements/YesOrNo'
import { Toast } from '@base-ui/react'

const Topbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  height: ${size(7)};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  padding: 0 ${size(1)};
  overflow: hidden;
  width: 100dvw;
  min-width: 0;
  background-color: black;
`

const TopbarLeft = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  min-width: 0;
;`
  
const TopbarRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  flex-shrink: 0;
`

const BackButton = styled(ButtonBase)`
  margin-right: ${size(1.5)};
  border-radius: 100%;
`

const UserLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
`

const TopbarUserName = styled.span`
  margin-left: ${size(1)};
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${fontSize('md')};
`

const ThumbnailContainer = styled.div`
  position: relative;
`

const Thumbnail = styled.img`
  height: ${size(3.75)};
  width: ${size(3.75)};
  border-radius: 100%;
  flex-shrink: 0;
`

const OnlineIndicator = styled.div`
  position: absolute;
  top: 15%;
  right: -7%;
  
  height: ${size(1)};
  width: ${size(1)};
  border-radius: 100px;
  
  flex-shrink: 0;
`

const MessagesTopbar = ({ otherUser, conversation, toggleFavorite, isNewConversation, userThumbnail, isAvatar }) => {
  const [isRemoveFavoriteDialogOpen, openRemoveFavoriteDialog, closeRemoveFavoriteDialog] = useYesOrNo('messages__conversation-remove-favorite')
  const { isOnline, isRecent } = isOnlineOrRecent(otherUser)
  const { openBottomDrawer } = useDrawerContext()
  const { state: authState } = useAuthContext()
  const toastManager = Toast.useToastManager()
  const { registerUser } = useModalContext()
  const location = useLocation()
  const navigate = useNavigate()

  const handleFavorite = (yesOrNo) => {
    if (yesOrNo) {
      const text = conversation.isFavorite ? 'removed from favorites' : 'added to favorites'
      toastManager.add({ text })
      toggleFavorite()
    }
    isRemoveFavoriteDialogOpen && closeRemoveFavoriteDialog()
  }

  const handleAddFavorite = () => {
    if (authState?.user?.isAnonymous) {
      registerUser.open({ search: { blocked_access: true } })
    } else if (!conversation.isLoading && !conversation?.isFavorite) {
      handleFavorite(true)
    }
  }

  const handleRemoveFavoriteOpen = () => {
    // Allow the user to destroy favorite regardless of subscription
    if (!conversation.isLoading && conversation?.isFavorite) {
      openRemoveFavoriteDialog()
    }
  }

  const profileLink = () => {
    if (isNewConversation || !location.pathname.includes('conversation')) {
      // NOTE: basically, we want to back navigate if we opened the messages from one one the profiles.
      //        It might be better to whitelist those in the future if we have to.
      return -1
    } else {
      return `/map?screen_modal=nearby-profile&nearby_uuid=${otherUser?.nearbyUuid}`
    }
  }

  return (
    <>
      <Topbar>
        <TopbarLeft>
          <BackButton onClick={() => navigate(-1)}>
            <PiCaretLeftBold size={size(3.5)} />
          </BackButton>
          {otherUser &&
            <UserLink to={profileLink()}>
              <ThumbnailContainer>
                <OnlineIndicator
                  style={{
                    backgroundColor: isOnline ? colors.onlineIndicator() : (isRecent ? colors.recentIndicator() : ''),
                    zIndex: 1,
                  }}
                />
                <Thumbnail
                  style={{ filter: isAvatar && 'grayscale(100%) contrast(120%)' }}
                  src={userThumbnail}
                />
              </ThumbnailContainer>
              <TopbarUserName>
                <AtAGlanceDetails
                  displayRegisteredIcon={false}
                  user={otherUser}
                  capitalizeEach
                  hasUserSuffix
                  wrappable={false}
                />
              </TopbarUserName>
            </UserLink>
          }
        </TopbarLeft>
        <TopbarRight>
          <IconButton
            disabled={!conversation}
            style={{ padding: size(0.5) }}
            onClick={conversation?.isFavorite ? handleRemoveFavoriteOpen : handleAddFavorite}>
            {conversation?.isFavorite
              ? <TbCarambolaFilled color={colors.statusColors.meetup('primary')} size={size(3)} />
              : <TbCarambola color='white' size={size(3)} />
            }
          </IconButton>
          <IconButton
            disabled={!conversation}
            style={{ padding: size(0.75) }}
            onClick={() => openBottomDrawer(<MessagesBottomDrawerContents otherUser={otherUser} />)}>
            <TbDotsVertical color='white' size={size(2.5)} />
          </IconButton>
        </TopbarRight>
      </Topbar>
      <YesOrNo
        isOpen={isRemoveFavoriteDialogOpen}
        setClosed={closeRemoveFavoriteDialog}
        handleSelect={handleFavorite}
        title='Remove from favorites?'
        body='Remove this conversation from your favorites? It may expire in the future.'
      />
    </>
  )
}

export default MessagesTopbar
